// The Creative Studio upload progress surface.
//
// WHAT THIS REPLACES
// ------------------
// A three-pixel bar, a filename, and a disabled button reading "Uploading 0/1…"
// — which the Acquire face rendered as "UPLOADING OF1...". During a real
// 716.6MB Never Stop Chasing master that was the entire feedback available for
// several minutes, and it could not distinguish a transfer in flight from a
// transfer that had finished and was waiting on the server.
//
// DESIGN RULES THIS COMPONENT ENFORCES
// ------------------------------------
//   * The determinate bar is shown ONLY while bytes are actually moving.
//     Verification, registration, and inspection get an indeterminate
//     treatment instead of a bar creeping from 99% to 100%.
//   * Percentages come from `percentFor`, which returns null rather than a
//     plausible number. There is no path in this file that invents progress.
//   * Every state is legible without colour: each row carries its stage in
//     words, not only as a hue.
//   * The live region announces STAGE CHANGES only. Percentages update in the
//     DOM but are never announced, so assistive technology is not flooded with
//     "63 percent, 64 percent, 65 percent" for the length of a large upload.
//
// All copy comes from `creativeUploadPresentation`, so Quantum and Cortex
// cannot drift apart in wording. This file is byte-identical across both.

import React, { useMemo } from "react";
import {
  UPLOAD_STAGES,
  describeRetryAction,
  describeRowStatus,
  describeUploadFailure,
  describeUploadStage,
  estimateTransfer,
  formatTimeRemaining,
  formatTransferRate,
  formatTransferred,
  isByteMeasurable,
  percentFor,
  summarizeUploadBatch,
} from "../utils/creativeUploadPresentation";
import "./CreativeUploadProgress.css";

/**
 * The rate/ETA pair, or empty strings.
 *
 * `estimateTransfer` withholds an estimate until the samples justify one, so
 * this quietly renders nothing early in a transfer rather than showing a
 * number that would visibly lurch on the next render.
 */
function useTransferReadout(entry) {
  return useMemo(() => {
    if (!isByteMeasurable(entry?.stage)) return { rate: "", remaining: "" };
    const estimate = estimateTransfer(entry?.samples, { total: entry?.total });
    if (!estimate) return { rate: "", remaining: "" };
    return {
      rate: formatTransferRate(estimate.bytesPerSecond),
      remaining: formatTimeRemaining(estimate.secondsRemaining),
    };
  }, [entry?.stage, entry?.samples, entry?.total]);
}

/**
 * The bar itself.
 *
 * Determinate and indeterminate are the SAME element with different ARIA:
 * omitting `aria-valuenow` is the standard way to say "in progress, amount
 * unknown", which is exactly true during verification and inspection.
 */
function UploadBar({ label, percent, valueText }) {
  const determinate = percent !== null && percent !== undefined;
  return (
    <div
      className={`cphere-upload-bar${determinate ? "" : " is-indeterminate"}`}
      role="progressbar"
      // Progress is visible on screen but must not be spoken on every tick.
      aria-live="off"
      aria-label={label}
      {...(determinate
        ? { "aria-valuenow": percent, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuetext": valueText }
        : {})}
    >
      <span
        className="cphere-upload-bar-fill"
        style={determinate ? { width: `${percent}%` } : undefined}
      />
    </div>
  );
}

/** One creative, presented in full. Used for a single-file upload. */
function UploadCard({ entry, count, onRetry }) {
  const stage = entry.stage || UPLOAD_STAGES.QUEUED;
  const percent = percentFor(entry);
  const transferred = formatTransferred(entry.loaded, entry.total);
  const { rate, remaining } = useTransferReadout(entry);
  const stageCopy = describeUploadStage(stage, { count });
  const failure = stage === UPLOAD_STAGES.FAILED ? describeUploadFailure(entry.error) : null;

  const headline = failure ? failure.headline : stageCopy.headline;
  const detail = failure ? failure.detail : stageCopy.detail;

  return (
    <article className={`cphere-upload-card is-${stage}`}>
      <p className="cphere-upload-card-name" title={entry.name}>
        {entry.name}
      </p>

      {/* Stage changes are material; byte counts are not. Only this node is
          live, which is what keeps announcements to roughly six per upload. */}
      <p className="cphere-upload-card-stage" aria-live="polite">
        {stage === UPLOAD_STAGES.COMPLETE ? (
          <span className="cphere-upload-check" aria-hidden="true" />
        ) : null}
        {headline}
      </p>
      <p className="cphere-upload-card-detail">{detail}</p>
      {/* "612.5 MB of 716.6 MB already saved." Resume is only offered against
          bytes the provider confirmed, so this states a fact rather than a
          hope — the distinction #252 exists to protect. */}
      {failure?.progressNote ? (
        <p className="cphere-upload-card-saved">{failure.progressNote}</p>
      ) : null}

      {percent !== null && stage !== UPLOAD_STAGES.COMPLETE ? (
        <div className="cphere-upload-card-readout">
          <span className="cphere-upload-percent">{percent}%</span>
          {transferred ? <span className="cphere-upload-transferred">{transferred}</span> : null}
        </div>
      ) : null}

      {stage === UPLOAD_STAGES.FAILED ? null : (
        <UploadBar
          label={`${entry.name} upload progress`}
          percent={percent}
          valueText={transferred ? `${percent}% — ${transferred}` : `${percent}%`}
        />
      )}

      {rate || remaining ? (
        <p className="cphere-upload-card-meta">{[rate, remaining].filter(Boolean).join(" · ")}</p>
      ) : null}

      {/* Only failures a retry could actually fix get one. Re-sending a file
          that exceeds the limit, or is the wrong format, will fail exactly the
          same way — offering the button would waste a 2.4GB transfer and imply
          the refusal was arbitrary. */}
      {failure?.action && onRetry ? (
        <button type="button" className="cphere-upload-retry" onClick={() => onRetry(entry)}>
          {failure.action}
        </button>
      ) : null}
    </article>
  );
}

/** One creative, presented compactly. Used inside a batch. */
function UploadRow({ entry, onRetry }) {
  const stage = entry.stage || UPLOAD_STAGES.QUEUED;
  const percent = percentFor(entry);
  const transferred = formatTransferred(entry.loaded, entry.total);
  const failure = stage === UPLOAD_STAGES.FAILED ? describeUploadFailure(entry.error) : null;

  return (
    <li className={`cphere-upload-row is-${stage}`}>
      <span className="cphere-upload-row-name" title={entry.name}>
        {entry.name}
      </span>
      <span className="cphere-upload-row-status">
        {stage === UPLOAD_STAGES.COMPLETE ? (
          <span className="cphere-upload-check" aria-hidden="true" />
        ) : null}
        {failure ? failure.headline : describeRowStatus(entry)}
      </span>

      {stage === UPLOAD_STAGES.FAILED ? null : (
        <UploadBar
          label={`${entry.name} upload progress`}
          percent={percent}
          valueText={transferred ? `${percent}% — ${transferred}` : `${percent}%`}
        />
      )}

      {stage === UPLOAD_STAGES.QUEUED ? (
        <span className="cphere-upload-row-detail">Waiting for an upload slot.</span>
      ) : null}
      {percent !== null && stage !== UPLOAD_STAGES.COMPLETE && transferred ? (
        <span className="cphere-upload-row-detail">{transferred}</span>
      ) : null}
      {failure ? (
        <span className="cphere-upload-row-detail cphere-upload-row-detail--failed">{failure.detail}</span>
      ) : null}
      {failure?.progressNote ? (
        <span className="cphere-upload-row-detail cphere-upload-row-saved">{failure.progressNote}</span>
      ) : null}
      {failure?.action && onRetry ? (
        <button type="button" className="cphere-upload-retry is-compact" onClick={() => onRetry(entry)}>
          {failure.action}
        </button>
      ) : null}
    </li>
  );
}

/**
 * The upload surface.
 *
 * One creative gets the full card. Several get a byte-weighted batch header
 * plus an independent row each — a 716.6MB file and a 275MB file must not
 * contribute equally to the overall bar, which is why the summary is computed
 * from byte sums rather than from a completed-file count.
 */
export default function CreativeUploadProgress({ entries = [], onRetryFile = null, onRetryAll = null }) {
  const list = (Array.isArray(entries) ? entries : []).filter(Boolean);
  const summary = useMemo(() => summarizeUploadBatch(list), [list]);
  // Same rule at batch level: a "retry failed uploads" button that would
  // re-send only files the policy already refused is a button that does
  // nothing.
  const retryableFailures = useMemo(
    () =>
      list.filter(
        (item) => item.stage === UPLOAD_STAGES.FAILED && describeUploadFailure(item.error).action
      ).length,
    [list]
  );

  if (!list.length) return null;

  if (list.length === 1) {
    return (
      <section className="cphere-upload-progress" aria-label="Creative upload progress">
        <UploadCard entry={list[0]} count={1} onRetry={onRetryFile} />
      </section>
    );
  }

  return (
    <section className="cphere-upload-progress" aria-label="Creative upload progress">
      <div className={`cphere-upload-batch${summary.active ? " is-active" : ""}`}>
        <p className="cphere-upload-batch-headline" aria-live="polite">
          {summary.headline}
        </p>
        <p className="cphere-upload-batch-detail">{summary.detail}</p>
        {/* On a settled batch the headline already IS the count ("4 OF 6
            CREATIVES UPLOADED"); repeating it underneath reads as a stutter. */}
        <p className="cphere-upload-batch-counts">
          {summary.countLabel !== summary.headline ? (
            <span className="cphere-upload-batch-count">{summary.countLabel}</span>
          ) : null}
          {summary.transferredLabel ? (
            <span className="cphere-upload-batch-transferred">{summary.transferredLabel}</span>
          ) : null}
        </p>
        <UploadBar
          label="Overall upload progress"
          percent={summary.percent}
          valueText={`${summary.percent}% — ${summary.transferredLabel}`}
        />
      </div>

      <ul className="cphere-upload-rows">
        {list.map((entry, index) => (
          <UploadRow key={`${entry.name}-${index}`} entry={entry} onRetry={onRetryFile} />
        ))}
      </ul>

      {retryableFailures > 0 && onRetryAll ? (
        <button type="button" className="cphere-upload-retry" onClick={onRetryAll}>
          {describeRetryAction(retryableFailures)}
        </button>
      ) : null}
    </section>
  );
}
